import React, {useState} from 'react';
import PropTypes from 'prop-types';
import debug from 'debug';
import styled from 'styled-components/macro';
import axiosInstance from '../utils/axios';
import qs from 'query-string';
import useSWR from 'swr';
import {Row, Columnizer, DescriptiveText, LoadingSpinner, InLineText} from '../index';
import Search from './Search/Search';

const log = debug('circleblack:investor:components:RowGrid');

const _Wrapper = styled.div`
  width: 100%;
  margin: 30px 0;
  padding-bottom: 20px;
`;

const _Spinner = styled.div`
  margin: 100px 0 0 0;
  padding-bottom: 20px;
`;

const RowGrid = ({excludedItems, excludedValueName, itemLimit, dataUrl, sortBy, sortAscending, createItem, search}) => {
  log('rendering');
  const builtParam = {
    sort: sortBy ? (sortAscending ? '+' : '-') + sortBy : null,
    [excludedValueName]: excludedItems.length ? (excludedItems.map(i => '!' + i[excludedValueName])) : null,
    limit: itemLimit || null,
  };
  const [lastSearch, setLastSearch] = useState('');
  const [param, setParam] = useState(builtParam);

  const {data: items} = useSWR([dataUrl, param], (url, p) => axiosInstance.get(dataUrl + '&' + qs.stringify(p)).then(res => res.data));

  if (!items) {
    return <_Spinner><LoadingSpinner/></_Spinner>;
  }

  return (
    <_Wrapper>
      <Row margin={'0 0 30px 0'} justifySubRow={'left'}>
        <InLineText
          margin={'0 10px 0 28px'}
          fontSize={'18px'}
          font={'titleText'}>
          My Feed
        </InLineText>
        {search && <Search searchOptions={search.searchOptions}
                           searchFunc={(optionName, value) => {
                             setParam(!value
                               ? {...param, [optionName]: null}
                               : {
                                 ...param,
                                 [optionName]: `~/${value}/`,
                               });
                             setLastSearch(value);
                           }}
                           defaultSearchValue={lastSearch}
                           margin={'10px'}
                           width={'fill'}
                           maxWidth={'350px'}
        />}
      </Row>
      {items.length
        ? <Columnizer alignSubRow={'stretch'} alignItems={'stretch'} showDividers={false} itemWidth={120} itemGrow={true} justifySubRow={'left'}>
          {items.map(itm => createItem(itm, 'fill', true))}
        </Columnizer>
        : <DescriptiveText
          margin={'20px'}
          fontSize={'18px'}
          font={'ProximaSemibold'}>
          {search.noSearchResultsText}
        </DescriptiveText>}
    </_Wrapper>
  );
};

export default RowGrid;

RowGrid.propTypes = {
  /**
   * items to be excluded from data fetch
   */
  excludedItems: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
  })),
  /**
   * Column name of value being excluded from data fetch
   */
  excludedValueName: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * maximum number of items to fetch and present in grid
   */
  itemLimit: PropTypes.number,
  /**
   * base url from which to retrieve data
   */
  dataUrl: PropTypes.string.isRequired,
  /**
   * data field to sort by
   */
  sortBy: PropTypes.string,
  /**
   * if true, sort ascending
   */
  sortAscending: PropTypes.bool,
  /**
   * function to create a row item
   */
  createItem: PropTypes.func.isRequired,
  /**
   * search params; if missing, no search bar is present
   */
  search: PropTypes.shape({
    /**
     * fields to search by; typically only one option
     */
    searchOptions: PropTypes.arrayOf(PropTypes.shape({
      title: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })).isRequired,
    /**
     * text to display when no search results are found
     */
    noSearchResultsText: PropTypes.string.isRequired,
  }),
};
